import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";

import { upcomingItems, plannable, firstMonth, monthOf, dayTitle, dayTime, stateText, todayKey } from "@/lib/v3Plans";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, EmptyState } from "../components/kit";
import { V3Title, V3Columns, V3Actions, DetailRow, DetailList, V3_PRIMARY } from "../components/v3";
import { AreaCard, ResponsibleCard, V3_LINK } from "../components/v3app";
import { usePlanItems, MonthCalendar, Blocked, enc } from "../components/v3plans";

const VARIANT = {
  calendar: { m: "M21", title: "Meine Pläne und Kalender", intro: "Bestätigte Termine und offene Anfragen haben unterschiedliche Kennzeichnungen.", cta: "Termin öffnen" },
  list: { m: "M93", title: "Meine Pläne als Liste", intro: "Die Liste enthält denselben Teilnahmestatus wie dein Kalender.", cta: "Termin ansehen" },
};

// M21 shows the next three entries, M93 all of them; both keep confirmed, requested and saved apart.
function PlansBody({ variant, res }) {
  const v = VARIANT[variant];
  const list = variant === "list";
  const today = todayKey();
  const items = res.items;
  const next = upcomingItems(items, today);
  const shown = list ? items : next.slice(0, 3);
  const [month, setMonth] = useState(() => firstMonth(items, today));
  const [selId, setSelId] = useState(() => (next[0] || items[0] || {}).id || "");
  const sel = items.find((i) => i.id === selId) || null;
  const choose = (i) => { setSelId(i.id); setMonth((m) => monthOf(i.day) || m); };
  const label = (i) => `${i.title} · ${stateText(i.state, list)}`;
  const target = !sel ? "" : list && sel.kind === "plan" ? `/plaene/${enc(sel.id)}` : `/aktivitaeten/${enc(sel.id)}`;
  const own = plannable(items);
  const base = sel && own.includes(sel) ? sel : own[0];
  const checklistTo = base ? `/plaene/checkliste?termin=${enc(base.id)}` : "/plaene/checkliste";
  const aside = <><AreaCard text={sel ? label(sel) : "Kein Termin ausgewählt"} testid="plans-area" /><ResponsibleCard /></>;
  return (
    <V3Columns layout="detail" aside={aside} main={<>
      {res.note && <div className="mb-[17px]"><Blocked id="plans-format-note">Die Pläne liefert dieser Core noch nicht. Angezeigt werden die Aktivitäten, bei denen du eingetragen bist oder angefragt hast.</Blocked></div>}
      <MonthCalendar items={items} month={month} onMonth={setMonth} selected={selId} onSelect={(dayItems) => setSelId(dayItems[0].id)} />
      {items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Noch keine Termine" text="Trag dich bei einer Aktivität ein oder merke dir eine Idee – sie erscheint dann hier." testid="plans-empty">
          <Link to="/aktivitaeten" className={V3_LINK}>Aktivitäten entdecken</Link>
        </EmptyState>
      ) : (
        <DetailList>
          {shown.length === 0 && <DetailRow label="Kommende Termine" value="Keine kommenden Termine" testid="plans-none-upcoming" />}
          {shown.map((i) => (
            <DetailRow key={i.id} label={list ? `${dayTime(i)}${i.day && i.day < today ? " · vergangen" : ""}` : dayTitle(i.day)} value={label(i)}
              onClick={() => choose(i)} pressed={i.id === selId} testid={`plan-${i.id}`} />
          ))}
          {list
            ? <DetailRow label="Weitere Planung" value="Eigene Checkliste öffnen" to={checklistTo} testid="plans-checklist" />
            : <DetailRow label="Ansicht" value="Kalender oder vollständige Terminliste" to="/plaene/liste" testid="plans-view" />}
        </DetailList>
      )}
      {res.savedMissing && <p className="mt-[11px] text-[13px] text-v3-muted" data-testid="plans-saved-missing">Die Merkliste ist gerade nicht abrufbar; gemerkte Ideen fehlen in dieser Ansicht.</p>}
      <V3Actions>
        <Link to="/plaene/geteilt" data-testid="plans-shared" className={V3_LINK}>Mit mir geteilte Anreisepläne</Link>
        {target ? <Link to={target} data-testid="plans-open" className={V3_PRIMARY}>{v.cta}<span className="sr-only">: {sel.title}</span></Link>
          : <button type="button" disabled data-testid="plans-open" className={V3_PRIMARY}>{v.cta}</button>}
      </V3Actions>
    </>} />
  );
}

function PlansView({ variant }) {
  const v = VARIANT[variant];
  const res = usePlanItems();
  return (
    <div data-m={v.m} data-testid="plans-page">
      <V3Title title={v.title} intro={v.intro} testid="plans-title" />
      {res.error ? <ConnectionError onRetry={res.reload} /> : !res.items ? <Loading /> : <PlansBody variant={variant} res={res} />}
    </div>
  );
}

export default function Plans() { return <PlansView variant="calendar" />; }
export function PlansList() { return <PlansView variant="list" />; }
